<template>
    <section>
        <e-autocomplete
            v-model="selected"
            :data="data"
            field="name"
            :set-value="setValue"
            :icon="includeIcon ? 'filter' : ''"
            :label="computedLabel"
            :help="help"
            :placeholder="computedPlaceholder"
            :can-add="canAdd"
            :show-add="showAdd"
            :url-params="'instructor'"
            :loading="isFetching"
            clearable
            @add-new="person => $emit('addNew', person)"
            @typing="getAsyncData"
            @select="person => $emit('update:modelValue', person)"
            @clear="$emit('update:modelValue', null)"
        >
            <template #default="props">
                <div class="tw:flex tw:justify-between tw:items-center tw:w-full tw:gap-2">
                    <div class="tw:flex tw:items-center tw:gap-2">
                        <user-avatar
                            :img-url="props.option.photo_url"
                            :title="props.option.name"
                            size="32x32"
                        />

                        <p>{{ props.option.name }}</p>
                    </div>

                    <div>
                        {{ props.option.email }}
                    </div>
                </div>
            </template>
        </e-autocomplete>
        <div v-if="error" class="error-message">{{ error }}</div>
    </section>
</template>

<script>
import debounce from 'lodash/debounce.js';
import { useI18n } from 'vue-i18n';

import Avatar from '@/components/elements/Avatar.vue';
import axios from '@/plugins/axios.js';

export default {
    name: 'UserSearch',

    components: { UserAvatar: Avatar },

    props: {
        modelValue: {
            type: Object,
            required: false,
            default: () => ({}),
        },

        label: {
            type: String,
            default: null,
            validator: prop => typeof prop === 'string' || prop === null,
        },

        help: {
            type: String,
            required: false,
            default: '',
        },

        canAdd: {
            type: Boolean,
            required: false,
            default: false,
        },

        customPlaceholder: {
            type: String,
            required: false,
            default: '',
        },

        showAdd: {
            type: Boolean,
            required: false,
            default: false,
        },

        showComputedLabel: {
            type: Boolean,
            required: false,
            default: true,
        },

        setUrlParams: {
            type: Boolean,
            required: false,
            default: false,
        },

        presetId: {
            type: Number,
            required: false,
            default: null,
        },

        includeIcon: {
            type: Boolean,
            required: false,
            default: false,
        },
        setValue: {
            type: Object,
            required: false,
            default: undefined,
        },
    },

    data() {
        return {
            data: [],
            selected: null,
            isFetching: false,
            error: null,
        };
    },

    setup() {
        const { t: translate } = useI18n();
        return { translate };
    },

    computed: {
        computedLabel() {
            if (this.showComputedLabel) {
                return typeof this.label === 'string' ? this.label : this.translate('select_a_person');
            }

            return null;
        },

        computedPlaceholder() {
            return this.customPlaceholder ? this.customPlaceholder : "Type a person's name...";
        },
    },

    watch: {
        setValue(newValue) {
            this.selected = newValue;
        },
    },

    async created() {
        this.selected = this.setValue;

        if (this.presetId) {
            await this.setInitialData();
        }
    },

    emits: ['add-new', 'update:modelValue', 'addNew'],

    methods: {
        getAsyncData: debounce(async function getAsyncData(name) {
            if (!name?.length) {
                this.data = [];
                return;
            }

            this.isFetching = true;
            this.error = null;

            try {
                const { data } = await axios.get(`/api/users?search=${name}`);
                this.data = data.data;
            } catch (error) {
                this.data = [];
                this.error = 'Failed to fetch users. Please try again.';
                console.error(error);
            } finally {
                this.isFetching = false;
            }
        }, 500),

        async setInitialData() {
            if (this.presetId) {
                try {
                    const { data } = await axios.get(`/api/users/${this.presetId}`);

                    this.selected = data.data.name;
                    this.$emit('update:modelValue', data.data);
                    this.error = null;
                } catch (error) {
                    this.error = 'Error loading user data. Please try again.';
                    console.error('Error loading user data:', error);
                }
            }
        },
    },
};
</script>

<style>
.error-message {
    color: red; /* Style the error message */
    margin-top: 0.5rem;
}
</style>
